import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { createUserWithEmailAndPassword, onIdTokenChanged, sendPasswordResetEmail, signInWithEmailAndPassword, signOut, updateProfile, type User } from "@firebase/auth";
import { auth, isFirebaseConfigured } from "./firebase";
import { localDemoEnabled } from "./local-demo";
import { authEmailSettings, configureAuthEmailLanguage, sendKateVerificationEmail } from "./auth-email";
import { verificationAwareToken } from "./auth-token";

type AuthContextValue = {
  user: User | null;
  loading: boolean;
  demo: boolean;
  login(email: string, password: string): Promise<void>;
  loginDemo(): Promise<void>;
  register(name: string, email: string, password: string): Promise<void>;
  reset(email: string): Promise<void>;
  logout(): Promise<void>;
  token(): Promise<string | null>;
};

const AuthContext = createContext<AuthContextValue | null>(null);
const demoEnabled = localDemoEnabled || !isFirebaseConfigured || import.meta.env.VITE_ENABLE_DEMO === "true";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [verified, setVerified] = useState(false);
  const [demo, setDemo] = useState(() => localDemoEnabled || (demoEnabled && localStorage.getItem("twm-demo") === "true"));
  const [loading, setLoading] = useState(isFirebaseConfigured && !localDemoEnabled);

  useEffect(() => {
    if (!auth) return void setLoading(false);
    // Firebase mutates the same User object when verification changes.
    return onIdTokenChanged(auth, value => { setUser(value); setVerified(value?.emailVerified === true); setLoading(false); });
  }, []);

  // Keep this callback stable when verification changes so account refreshes do not reset editor drafts.
  const token = useCallback(async () => user ? verificationAwareToken(user, () => auth?.currentUser === user) : null, [user]);

  const value = useMemo<AuthContextValue>(() => ({
    user,
    demo,
    loading,
    async loginDemo() {
      if (!demoEnabled) throw new Error("Demo mode is disabled");
      localStorage.setItem("twm-demo", "true"); setDemo(true);
    },
    async login(email, password) {
      if (!auth) { localStorage.setItem("twm-demo", "true"); setDemo(true); return; }
      await signInWithEmailAndPassword(auth, email, password);
    },
    async register(name, email, password) {
      if (!auth) { localStorage.setItem("twm-demo", "true"); setDemo(true); return; }
      const result = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(result.user, { displayName: name });
      try { await sendKateVerificationEmail(result.user); }
      catch { console.warn("Email verification could not be sent. Request it again from Subscription."); }
    },
    async reset(email) {
      if (!auth) return;
      configureAuthEmailLanguage();
      try { await sendPasswordResetEmail(auth, email, authEmailSettings("/login")); }
      catch (cause) {
        // Do not disclose whether an email address is registered. Firebase still controls rate limits and tokens.
        if (!(cause && typeof cause === "object" && "code" in cause && cause.code === "auth/user-not-found")) throw cause;
      }
    },
    async logout() {
      if (auth) await signOut(auth);
      localStorage.removeItem("twm-demo"); setDemo(false);
    },
    token
  }), [user, demo, loading, verified, token]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used within AuthProvider");
  return value;
}
